import { useEffect } from 'react';

import { PanelScopeProvider } from '../contexts/PanelScopeContext';
import { usePanelNavigation } from '../stores/usePanelNavigation';
import { usePanelPreferenceStore } from '../stores/usePanelPreferenceStore';
import { usePanelScopeRegistry } from '../stores/usePanelScopeRegistry';
import { useSelectionChannel } from '../stores/useSelectionChannel';

import type { SelectionData } from '../stores/useSelectionChannel';

/**
 * DetailScopeProvider Props
 */
interface DetailScopeProviderProps {
  /**
   * 자식 컴포넌트
   */
  children: React.ReactNode;

  /**
   * Scope 키
   */
  scopeKey: string;
}

/**
 * Detail Scope를 초기화하고 SelectionChannel → PanelNavigation으로 연결합니다.
 * @description
 * - Registry에서 scope 설정을 검증하고 selection을 구독합니다.
 * - selection 발생 시 viewMode='standalone'으로 detail 패널을 엽니다.
 * - 언마운트 시 resetScope/clearSelection으로 정리합니다.
 * @example
 * ```tsx
 * <DetailScopeProvider scopeKey="dns/zone/detail">
 *   <ZoneDetailContent>
 *     <RecordTable panelMode="standalone" />
 *   </ZoneDetailContent>
 * </DetailScopeProvider>
 * ```
 */
export default function DetailScopeProvider({ scopeKey, children }: DetailScopeProviderProps) {
  const { getScopeConfig } = usePanelScopeRegistry();
  const { subscribeSelection, clearSelection } = useSelectionChannel();
  const { openSidePanel, closePanel, resetScope } = usePanelNavigation();

  // V2 패널 환경설정: detail 패널 활성화 여부 확인
  const detailEnabled = usePanelPreferenceStore(state => state.preferences.detail.enabled);
  const detailAvailable = usePanelPreferenceStore(state => state.availability.detail);

  useEffect(() => {
    // 1. Registry에서 config 조회
    const config = getScopeConfig(scopeKey);
    if (!config) {
      console.error(
        `[DetailScopeProvider] Scope ${scopeKey} not registered — skipping subscription`,
      );
      return;
    }

    // 2. SelectionChannel 구독
    const unsubscribe = subscribeSelection(scopeKey, (selection: SelectionData | null) => {
      // selection이 클리어되면 패널 닫기
      if (!selection) {
        closePanel(scopeKey);
        return;
      }

      // 패널이 비활성화되어 있으면 열지 않음
      if (!detailEnabled || !detailAvailable) {
        return;
      }

      // 3. Provider가 viewMode 결정 (항상 'standalone')
      // 4. Registry에서 panelResource 조회
      // Phase 6: openDetailPanel → openSidePanel 리네이밍
      openSidePanel({
        scopeKey,
        panelResource: config.panelResource,
        panelId: selection.ids[0], // Phase 2: single only
        viewMode: 'standalone',
      });
    });

    // 5. cleanup
    return () => {
      unsubscribe();
      resetScope(scopeKey);
      clearSelection(scopeKey);
    };
  }, [
    scopeKey,
    getScopeConfig,
    subscribeSelection,
    openSidePanel,
    closePanel,
    resetScope,
    clearSelection,
    detailEnabled,
    detailAvailable,
  ]);

  return <PanelScopeProvider scopeKey={scopeKey}>{children}</PanelScopeProvider>;
}
